import { ResizeNotifier, preventDefault, prefersReducedMotion } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';

/** @constant {number} Minimum pointer movement (px) before a scroll drag starts */
const SCROLL_DRAG_THRESHOLD = 10;

/** @constant {number} Subpixel tolerance when detecting scroll edges */
const SCROLL_EDGE_EPSILON = 2;

/** @constant {number} Weight retained from the previous touch velocity sample */
const TOUCH_VELOCITY_SMOOTHING = 0.6;

/** @constant {number} Ignore release velocity when the final movement sample is stale */
const TOUCH_VELOCITY_STALE_MS = 100;

/** @constant {number} Maximum momentum velocity in pixels per millisecond */
const TOUCH_MAX_VELOCITY = 1.5;

/** @constant {number} Momentum velocity retained per 60 Hz frame */
const TOUCH_MOMENTUM_DECAY = 0.92;

/** @constant {number} Stop momentum below this velocity in pixels per millisecond */
const TOUCH_MOMENTUM_STOP_VELOCITY = 0.02;

/** @constant {number} Duration of a 60 Hz animation frame */
const TOUCH_FRAME_MS = 1000 / 60;

/** @constant {number} Ignore compatibility mouse events after a touch for this long */
const TOUCH_MOUSE_SUPPRESS_MS = 1500;

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section and other areas.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @attr {string | null} disabled When `"true"`, skips clipping and enables horizontal scroll/drag (and optional nav arrows).
 * @attr {string | null} nav-arrows When present with `disabled="true"`, shows prev/next scroll controls.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (!this.#refs) return;

      if (newValue === 'true') {
        this.#reset();
        this.#setupScrollMode();
      } else {
        this.#teardownScrollMode();
        this.#reflowItems();
      }
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    // Styles for dynamically injected <overflow-list> elements are async.
    // We need to wait for them to be loaded before initializing the element to properly calculate the overflow.
    await this.#waitForStyles();

    this.#initialize();
  }

  #waitForStyles() {
    /** @type {HTMLLinkElement | null | undefined} */
    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

    // No styles or styles are already loaded.
    if (!styles || styles.sheet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      styles.addEventListener('load', resolve);
    });
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');
    const previous = shadowRoot.querySelector('[part="previous"]');
    const next = shadowRoot.querySelector('[part="next"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
      previous: previous instanceof HTMLButtonElement ? previous : null,
      next: next instanceof HTMLButtonElement ? next : null,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event */ (event) => {
        this.#reflowItems(0, event.detail.lastVisibleElement);
      }
    );

    // When <overflow-list> is dynamically injected, the browser doesn't remove its <template> automatically.
    // In theory, we could get rid of it now, or in DeclarativeShadowElement, but that would invalidate the layout.
    // Instead, we ignore it for now and remove it later on the first reflow.
    const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    // Observe the first and last elements to trigger a reflow when they are visible.
    // That way we can get their height from the IntersectionObserver for free (without reflows).
    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }
    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }

    if (this.getAttribute('disabled') === 'true') {
      this.#setupScrollMode();
    }
  }

  disconnectedCallback() {
    this.#teardownScrollMode();
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (entry?.isIntersecting) {
      this.#intersectionObserver.disconnect();
      setTimeout(() => {
        // Remove the leftover <template> for dynamically injected <overflow-list> elements.
        this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();

        if (this.getAttribute('disabled') === 'true') {
          this.#setupScrollMode();
          return;
        }

        this.#reflowItems(entry.boundingClientRect.height);
      }, 0);
    }
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = () => {
    if (this.#scheduled) return;

    this.#scheduled = true;

    requestAnimationFrame(() =>
      setTimeout(() => {
        this.#reflowItems();
        this.#scheduled = false;
      }, 0)
    );
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    if (!this.#refs?.list) return;

    const { list } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Scroll a slotted child into view inside the shadow scrollport.
   * @param {Element} element
   * @param {{ inline?: ScrollLogicalPosition, behavior?: ScrollBehavior }} [options]
   * @param {number} [attempt]
   */
  scrollToChild(element, { inline = 'center', behavior = 'smooth' } = {}, attempt = 0) {
    if (!(element instanceof HTMLElement)) return;
    if (!this.contains(element)) return;

    this.#cancelTouchInteraction();

    const list = this.#refs?.list;
    if (!list) {
      if (!this.isConnected || attempt > 4) return;

      requestAnimationFrame(() => this.scrollToChild(element, { inline, behavior }, attempt + 1));
      return;
    }

    const styles = getComputedStyle(list);
    const paddingStart = Number.parseFloat(styles.scrollPaddingInlineStart) || 0;
    const paddingEnd = Number.parseFloat(styles.scrollPaddingInlineEnd) || 0;
    const elemRect = element.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    const visibleLeft = listRect.left + paddingStart;
    const visibleRight = listRect.right - paddingEnd;

    if (
      elemRect.left >= visibleLeft - SCROLL_EDGE_EPSILON &&
      elemRect.right <= visibleRight + SCROLL_EDGE_EPSILON
    ) {
      return;
    }

    scrollIntoView(element, { ancestor: list, inline, behavior });
  }

  /**
   * Reflow items based on available space within the list.
   * @param {number} [listHeight] Initial height of the list
   * @param {HTMLElement | null} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    if (this.getAttribute('disabled') === 'true') {
      this.#updateNavArrows();
      return;
    }

    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#unobserveChanges();

    // Reset all elements to the default slot so we can check which ones overflow.
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    const lastElement = elements[elements.length - 1];

    if (!lastElement) {
      this.#observeChanges();
      return;
    }

    /** @type {Element[]} */
    let visibleElements = [];
    /** @type {Element[]} */
    let overflowingElements = [];
    let placeholderWidth = 0;
    let hasOverflow = false;

    if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    }

    // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row.
    moreSlot.style.setProperty('order', '-1');
    moreSlot.hidden = false;

    lastVisibleElement?.style.setProperty('order', '-1');

    const moreSlotRect = moreSlot.getBoundingClientRect();

    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();

      if (elementRect.top > moreSlotRect.top) {
        if (!overflowingElements.length) {
          placeholderWidth = elementRect.width;
        }

        hasOverflow = true;
        overflowingElements.push(element);
      } else {
        visibleElements.push(element);
      }
    });

    if (hasOverflow) {
      moreSlot.style.removeProperty('order');
    }
    lastVisibleElement?.style.removeProperty('order');

    // Move the elements to the correct slot.
    for (const element of elements) {
      const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

    // Adjust the "More" button visibility.
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space.
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    // Reset the overflow property since children elements may need to display outside the list (e.g. dropdowns, popovers).
    list.style.setProperty('overflow', 'unset');

    hasOverflow && this.#updateMinimumReached(visibleElements);

    this.#observeChanges();
  };

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   *   previous: HTMLButtonElement | null;
   *   next: HTMLButtonElement | null;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);

  #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
    // Extend the root margin to around one more viewport of a typical mobile screen.
    rootMargin: '640px 360px 640px 360px',
  });

  /** @type {AbortController | null} */
  #scrollDragAbort = null;

  /** @type {AbortController | null} */
  #navArrowsAbort = null;

  /** @type {boolean} */
  #scrollDragging = false;

  /** @type {AbortController | null} */
  #touchGestureAbort = null;

  /** @type {number | null} */
  #touchScrollFrame = null;

  /** @type {number | null} */
  #touchScrollTarget = null;

  /** @type {number | null} */
  #touchMomentumFrame = null;

  /** @type {number} */
  #touchMomentumVelocity = 0;

  /** @type {number} */
  #touchMomentumTimestamp = 0;

  /** @type {boolean} */
  #touchScrollActive = false;

  /** @type {number | null} */
  #touchNavArrowFrame = null;

  /** @type {number} */
  #lastTouchAt = 0;

  #setupScrollMode() {
    this.#setupScrollDrag();
    this.#setupNavArrows();
  }

  #teardownScrollMode() {
    this.#teardownScrollDrag();
    this.#teardownNavArrows();
  }

  #setupScrollDrag() {
    if (this.getAttribute('disabled') !== 'true') return;
    if (this.#scrollDragAbort) return;
    if (!this.#refs) return;

    const { list } = this.#refs;
    if (!list) return;

    this.#scrollDragAbort = new AbortController();
    const { signal } = this.#scrollDragAbort;

    // Desktop click-drag uses capture mousedown so we can delay radio activation until
    // mouseup. pointerdown + setPointerCapture on this host cancels the pointer when the
    // hit target is a slotted radio/label. Touch stays on a separate path for iOS WebKit.
    this.addEventListener('mousedown', this.#handleScrollDragMouseDown, { signal, capture: true });
    this.addEventListener('touchstart', this.#handleScrollDragTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#markTouchEnd, { signal, passive: true });
    this.addEventListener('touchcancel', this.#markTouchEnd, { signal, passive: true });
  }

  #teardownScrollDrag() {
    this.#cancelTouchInteraction();
    this.#scrollDragAbort?.abort();
    this.#scrollDragAbort = null;
    this.#scrollDragging = false;

    const { list } = this.#refs ?? {};
    this.removeAttribute('dragging');
    list?.removeAttribute('dragging');
  }

  #setupNavArrows() {
    if (this.getAttribute('disabled') !== 'true') return;
    if (!this.hasAttribute('nav-arrows')) return;
    if (this.#navArrowsAbort) return;
    if (!this.#refs) return;

    const { list, previous, next } = this.#refs;
    if (!list || !previous || !next) return;

    this.#navArrowsAbort = new AbortController();
    const { signal } = this.#navArrowsAbort;

    const { defaultSlot } = this.#refs;

    previous.addEventListener('click', this.#handlePreviousClick, { signal });
    next.addEventListener('click', this.#handleNextClick, { signal });
    list.addEventListener('scroll', this.#handleListScroll, { signal, passive: true });
    defaultSlot.addEventListener('slotchange', this.#updateNavArrows, { signal });

    const resizeObserver = new ResizeObserver(() => {
      this.#updateNavArrows();
    });
    resizeObserver.observe(list);
    signal.addEventListener('abort', () => resizeObserver.disconnect());

    this.#updateNavArrows();
  }

  #teardownNavArrows() {
    this.#navArrowsAbort?.abort();
    this.#navArrowsAbort = null;
    if (this.#touchNavArrowFrame !== null) {
      cancelAnimationFrame(this.#touchNavArrowFrame);
      this.#touchNavArrowFrame = null;
    }
    this.removeAttribute('data-can-scroll');

    const { previous, next } = this.#refs ?? {};
    if (previous) previous.disabled = true;
    if (next) next.disabled = true;
  }

  #getScrollStep() {
    const { list, defaultSlot } = this.#refs;
    const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
    const firstItem = elements[0];

    if (firstItem instanceof HTMLElement) {
      const styles = getComputedStyle(list);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return firstItem.getBoundingClientRect().width + gap;
    }

    return list.clientWidth * 0.8;
  }

  #updateNavArrows = () => {
    if (!this.#refs) return;

    const { list, previous, next } = this.#refs;
    if (!list || !previous || !next) return;

    const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    const canScroll = maxScrollLeft > SCROLL_EDGE_EPSILON;
    const scrollLeft = Math.min(maxScrollLeft, Math.max(0, list.scrollLeft));

    // WebKit can report and retain a scroll position beyond either edge while
    // elastic overscroll is active. Keep the rendered position in range too.
    if (!this.#touchScrollActive && list.scrollLeft !== scrollLeft) {
      list.scrollLeft = scrollLeft;
    }

    const canScrollValue = canScroll ? 'true' : 'false';
    if (this.getAttribute('data-can-scroll') !== canScrollValue) {
      this.setAttribute('data-can-scroll', canScrollValue);
    }

    const previousDisabled = !canScroll || scrollLeft <= SCROLL_EDGE_EPSILON;
    const nextDisabled = !canScroll || scrollLeft >= maxScrollLeft - SCROLL_EDGE_EPSILON;

    if (previous.disabled !== previousDisabled) previous.disabled = previousDisabled;
    if (next.disabled !== nextDisabled) next.disabled = nextDisabled;
  };

  #handleListScroll = () => {
    if (!this.#touchScrollActive) {
      this.#updateNavArrows();
      return;
    }

    if (this.#touchNavArrowFrame !== null) return;

    this.#touchNavArrowFrame = requestAnimationFrame(() => {
      this.#touchNavArrowFrame = null;
      this.#updateNavArrows();
    });
  };

  #handlePreviousClick = (event) => {
    event.preventDefault();
    this.#scrollByStep(-1);
  };

  #handleNextClick = (event) => {
    event.preventDefault();
    this.#scrollByStep(1);
  };

  /**
   * Scroll one item in either direction without requesting a position outside
   * the scrollable range. Safari can expose out-of-range scrollLeft values
   * during elastic overscroll, so clamp both the starting point and target.
   * @param {-1 | 1} direction
   */
  #scrollByStep(direction) {
    const { list } = this.#refs;
    if (!list) return;

    this.#cancelTouchInteraction();

    const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    const scrollLeft = Math.min(maxScrollLeft, Math.max(0, list.scrollLeft));
    const target = Math.min(maxScrollLeft, Math.max(0, scrollLeft + direction * this.#getScrollStep()));

    list.scrollTo({ left: target, behavior: 'smooth' });
  }

  /**
   * @param {MouseEvent} event
   */
  #handleScrollDragMouseDown = (event) => {
    if (this.getAttribute('disabled') !== 'true') return;
    if (!(event.target instanceof Element)) return;
    if (event.button !== 0) return;
    if (this.#touchGestureAbort || this.#touchScrollActive) return;
    if (this.#isEmulatedMouseFromTouch(event)) return;

    // Nav arrows live in the shadow root; use composedPath because target is retargeted at the host.
    const isNavControl = event.composedPath().some(
      (node) =>
        node instanceof Element &&
        (node.getAttribute('part') === 'previous' ||
          node.getAttribute('part') === 'next' ||
          node.getAttribute('part') === 'nav')
    );
    if (isNavControl) return;

    const { list } = this.#refs;
    if (!list) return;

    const radio = this.#getScrollDragRadio(event);
    const previouslyChecked = radio ? this.#getCheckedRadio(radio) : null;

    // Capture-phase preventDefault stops Safari/Chrome from checking the radio on press.
    if (radio) event.preventDefault();

    const controller = new AbortController();
    const clickController = new AbortController();
    const { signal } = controller;
    const startX = event.clientX;
    const startY = event.clientY;
    let previousX = event.clientX;
    /** @type {'pending' | 'drag' | 'cancel'} */
    let outcome = 'pending';

    const cleanupClickSuppressor = () => {
      setTimeout(() => clickController.abort(), 0);
    };

    if (radio) {
      // Must outlive pointerup: native click is dispatched after pointerup handlers abort.
      document.addEventListener(
        'click',
        (clickEvent) => {
          clickEvent.preventDefault();
        },
        { capture: true, once: true, signal: clickController.signal }
      );
    }

    /**
     * @param {PointerEvent} moveEvent
     */
    const onPointerMove = (moveEvent) => {
      if (outcome === 'cancel') return;

      if (outcome === 'pending') {
        const totalX = Math.abs(moveEvent.clientX - startX);
        const totalY = Math.abs(moveEvent.clientY - startY);

        if (totalY > SCROLL_DRAG_THRESHOLD && totalY > totalX) {
          outcome = 'cancel';
          controller.abort();
          cleanupClickSuppressor();
          return;
        }

        if (totalX < SCROLL_DRAG_THRESHOLD) return;

        outcome = 'drag';
        this.#scrollDragging = true;
        this.setAttribute('dragging', '');
        list.setAttribute('dragging', '');
        this.#revertRadioIfNeeded(radio, previouslyChecked);
      }

      const delta = previousX - moveEvent.clientX;
      previousX = moveEvent.clientX;

      if (delta) {
        moveEvent.preventDefault();
        list.scrollLeft += delta;
      }
    };

    /**
     * @param {PointerEvent} endEvent
     */
    const onPointerEnd = (endEvent) => {
      controller.abort();
      this.#scrollDragging = false;
      this.removeAttribute('dragging');
      list.removeAttribute('dragging');

      if (endEvent.type === 'pointerup' && outcome === 'pending') {
        this.#commitRadioSelection(radio);
      }

      cleanupClickSuppressor();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerEnd, { signal });
    document.addEventListener('pointercancel', onPointerEnd, { signal });
    document.addEventListener('pointerdown', onPointerEnd, { signal });
  };

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | null}
   */
  #getScrollDragRadio(event) {
    for (const node of event.composedPath()) {
      if (node instanceof HTMLInputElement && node.type === 'radio') return node;

      if (node instanceof HTMLLabelElement) {
        const labeled =
          node.control instanceof HTMLInputElement ? node.control : node.querySelector('input[type="radio"]');

        if (labeled instanceof HTMLInputElement && labeled.type === 'radio') return labeled;
      }
    }

    return null;
  }

  /**
   * @param {HTMLInputElement} radio
   * @returns {HTMLInputElement | null}
   */
  #getCheckedRadio(radio) {
    const scope = radio.closest('fieldset') ?? this;

    for (const input of scope.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement && input.name === radio.name && input.checked) return input;
    }

    return null;
  }

  /**
   * @param {HTMLInputElement | null} radio
   * @param {HTMLInputElement | null} previouslyChecked
   */
  #revertRadioIfNeeded(radio, previouslyChecked) {
    if (!radio || radio === previouslyChecked || !radio.checked) return;

    radio.checked = false;
    if (previouslyChecked) previouslyChecked.checked = true;
  }

  /**
   * @param {HTMLInputElement | null} radio
   */
  #commitRadioSelection(radio) {
    if (!radio || radio.disabled) return;
    if (radio.checked) return;

    radio.checked = true;
    radio.focus({ preventScroll: true });
    radio.dispatchEvent(new Event('input', { bubbles: true }));
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #markTouchEnd = () => {
    this.#lastTouchAt = performance.now();
  };

  /**
   * iOS/Android fire compatibility mousedown/click after touchend. Capture preventDefault
   * on that mousedown would swallow the tap, and there is no second pointerup to commit.
   * @param {MouseEvent} event
   */
  #isEmulatedMouseFromTouch(event) {
    if (event.sourceCapabilities?.firesTouchEvents) return true;

    return performance.now() - this.#lastTouchAt < TOUCH_MOUSE_SUPPRESS_MS;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleScrollDragTouchStart = (event) => {
    this.#markTouchEnd();
    if (this.getAttribute('disabled') !== 'true') return;
    if (event.touches.length !== 1) {
      this.#cancelTouchInteraction();
      return;
    }

    this.#cancelTouchInteraction();

    const isNavControl = event.composedPath().some(
      (node) =>
        node instanceof Element &&
        (node.getAttribute('part') === 'previous' ||
          node.getAttribute('part') === 'next' ||
          node.getAttribute('part') === 'nav')
    );
    if (isNavControl) return;

    const { list } = this.#refs;
    if (!list || list.scrollWidth <= list.clientWidth) return;

    const touch = event.touches[0];
    if (!touch) return;

    const controller = new AbortController();
    const { signal } = controller;
    const touchIdentifier = touch.identifier;
    const startX = touch.clientX;
    const startY = touch.clientY;
    const startScrollLeft = list.scrollLeft;
    const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    let previousTarget = startScrollLeft;
    let previousTime = performance.now();
    let lastMoveTime = previousTime;
    let velocity = 0;
    let moved = false;

    this.#touchGestureAbort = controller;
    this.#scrollDragAbort?.signal.addEventListener('abort', () => controller.abort(), { once: true });

    /**
     * @param {TouchList} touches
     */
    const findTouch = (touches) => Array.from(touches).find((item) => item.identifier === touchIdentifier);

    /**
     * @param {TouchEvent} moveEvent
     */
    const onTouchMove = (moveEvent) => {
      if (moveEvent.touches.length !== 1) {
        this.#cancelTouchInteraction();
        return;
      }

      const currentTouch = findTouch(moveEvent.touches);
      if (!currentTouch) return;

      if (!moved) {
        const totalX = Math.abs(currentTouch.clientX - startX);
        const totalY = Math.abs(currentTouch.clientY - startY);

        if (totalY > SCROLL_DRAG_THRESHOLD && totalY > totalX) {
          controller.abort();
          if (this.#touchGestureAbort === controller) this.#touchGestureAbort = null;
          return;
        }

        if (totalX < SCROLL_DRAG_THRESHOLD || totalX <= totalY) return;

        moved = true;
        this.#scrollDragging = true;
        this.#touchScrollActive = true;
        this.setAttribute('dragging', '');
        list.setAttribute('dragging', '');
        document.addEventListener('click', preventDefault, { once: true, signal });
      }

      moveEvent.preventDefault();

      const rawDelta = startX - currentTouch.clientX;
      const adjustedDelta = Math.sign(rawDelta) * Math.max(0, Math.abs(rawDelta) - SCROLL_DRAG_THRESHOLD);
      const target = Math.min(maxScrollLeft, Math.max(0, startScrollLeft + adjustedDelta));
      const now = performance.now();
      const elapsed = now - previousTime;

      if (elapsed > 0) {
        const sampleVelocity = (target - previousTarget) / elapsed;
        velocity = velocity * TOUCH_VELOCITY_SMOOTHING + sampleVelocity * (1 - TOUCH_VELOCITY_SMOOTHING);
      }

      previousTarget = target;
      previousTime = now;
      lastMoveTime = now;
      this.#queueTouchScroll(target);
    };

    /**
     * @param {TouchEvent} endEvent
     */
    const onTouchEnd = (endEvent) => {
      if (!findTouch(endEvent.changedTouches)) return;

      controller.abort();
      if (this.#touchGestureAbort === controller) this.#touchGestureAbort = null;
      this.#scrollDragging = false;
      this.removeAttribute('dragging');
      list.removeAttribute('dragging');

      if (!moved) return;

      endEvent.preventDefault();

      if (endEvent.type === 'touchcancel') {
        this.#touchMomentumVelocity = 0;
        if (this.#touchScrollFrame === null) this.#finishTouchMotion();
        return;
      }

      const velocityIsFresh = performance.now() - lastMoveTime <= TOUCH_VELOCITY_STALE_MS;
      if (
        prefersReducedMotion() ||
        !velocityIsFresh ||
        Math.abs(velocity) < TOUCH_MOMENTUM_STOP_VELOCITY
      ) {
        this.#touchMomentumVelocity = 0;
        if (this.#touchScrollFrame === null) this.#finishTouchMotion();
        return;
      }

      this.#touchMomentumVelocity = Math.min(TOUCH_MAX_VELOCITY, Math.max(-TOUCH_MAX_VELOCITY, velocity));
      this.#touchMomentumTimestamp = performance.now();

      if (this.#touchScrollFrame === null) this.#scheduleTouchMomentum();
    };

    document.addEventListener('touchmove', onTouchMove, { signal, passive: false });
    document.addEventListener('touchend', onTouchEnd, { signal, passive: false });
    document.addEventListener('touchcancel', onTouchEnd, { signal, passive: false });
  };

  /**
   * Apply the latest finger position at most once per animation frame.
   * @param {number} target
   */
  #queueTouchScroll(target) {
    this.#touchScrollTarget = target;
    if (this.#touchScrollFrame !== null) return;

    this.#touchScrollFrame = requestAnimationFrame(() => {
      this.#touchScrollFrame = null;
      const nextTarget = this.#touchScrollTarget;
      this.#touchScrollTarget = null;

      const { list } = this.#refs;
      if (list && nextTarget !== null) list.scrollLeft = nextTarget;

      if (this.#touchMomentumVelocity) {
        this.#scheduleTouchMomentum();
      } else if (!this.#touchGestureAbort) {
        this.#finishTouchMotion();
      }
    });
  }

  #scheduleTouchMomentum() {
    if (this.#touchMomentumFrame !== null || !this.#touchMomentumVelocity) return;

    this.#touchMomentumFrame = requestAnimationFrame(this.#runTouchMomentum);
  }

  /**
   * Continue a touch flick with bounded, frame-rate-independent momentum.
   * @param {DOMHighResTimeStamp} timestamp
   */
  #runTouchMomentum = (timestamp) => {
    this.#touchMomentumFrame = null;

    const { list } = this.#refs;
    if (!list || !this.#touchMomentumVelocity) {
      this.#finishTouchMotion();
      return;
    }

    const elapsed = Math.max(1, Math.min(50, timestamp - this.#touchMomentumTimestamp));
    this.#touchMomentumTimestamp = timestamp;

    const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    const currentScrollLeft = Math.min(maxScrollLeft, Math.max(0, list.scrollLeft));
    const target = Math.min(
      maxScrollLeft,
      Math.max(0, currentScrollLeft + this.#touchMomentumVelocity * elapsed)
    );

    list.scrollLeft = target;
    this.#touchMomentumVelocity *= Math.pow(TOUCH_MOMENTUM_DECAY, elapsed / TOUCH_FRAME_MS);

    const reachedStart = target <= SCROLL_EDGE_EPSILON && this.#touchMomentumVelocity < 0;
    const reachedEnd = target >= maxScrollLeft - SCROLL_EDGE_EPSILON && this.#touchMomentumVelocity > 0;

    if (
      reachedStart ||
      reachedEnd ||
      Math.abs(this.#touchMomentumVelocity) < TOUCH_MOMENTUM_STOP_VELOCITY
    ) {
      this.#finishTouchMotion();
      return;
    }

    this.#scheduleTouchMomentum();
  };

  #finishTouchMotion() {
    this.#touchMomentumVelocity = 0;
    this.#touchMomentumTimestamp = 0;
    this.#touchScrollActive = false;

    if (this.#touchNavArrowFrame !== null) {
      cancelAnimationFrame(this.#touchNavArrowFrame);
      this.#touchNavArrowFrame = null;
    }

    this.#updateNavArrows();
  }

  #cancelTouchInteraction() {
    const hadTouchInteraction =
      this.#touchGestureAbort !== null ||
      this.#touchScrollFrame !== null ||
      this.#touchMomentumFrame !== null ||
      this.#touchScrollActive;

    this.#touchGestureAbort?.abort();
    this.#touchGestureAbort = null;

    if (this.#touchScrollFrame !== null) {
      cancelAnimationFrame(this.#touchScrollFrame);
      this.#touchScrollFrame = null;
    }

    if (this.#touchMomentumFrame !== null) {
      cancelAnimationFrame(this.#touchMomentumFrame);
      this.#touchMomentumFrame = null;
    }

    if (this.#touchNavArrowFrame !== null) {
      cancelAnimationFrame(this.#touchNavArrowFrame);
      this.#touchNavArrowFrame = null;
    }

    this.#touchScrollTarget = null;
    this.#touchMomentumVelocity = 0;
    this.#touchMomentumTimestamp = 0;
    this.#touchScrollActive = false;

    if (!hadTouchInteraction) return;

    this.#scrollDragging = false;
    this.removeAttribute('dragging');
    this.#refs?.list?.removeAttribute('dragging');
    this.#updateNavArrows();
  }
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
